import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AdminAuthAPI, AdminTokenService } from './services/api';

// Import components
import Login from './pages/auth/Login';
import Dashboard from './pages/Dashboard';
import UsersPage from './pages/Users';
import UserDetail from './pages/UserDetail';
import WalletDetail from './pages/WalletDetail';
import KYC from './pages/KYC';
import KYCDetail from './pages/KYCDetail';
import Settings from './pages/Settings';
import Admins from './pages/Admins';
import AdminDetail from './pages/AdminDetail';
import Layout from './components/common/Layout';
import Loading from './components/common/Loading';
import WalletsPage from './pages/Wallet';
import WalletSettings from './pages/WalletSettings';
import DepositManagement from './pages/DepositManagement';
import WithdrawalManagement from './pages/WithdrawalManagement';
import BTCSettings from './pages/BTCSettings';
import Notifications from './pages/Notifications';
import Bets from './pages/Bets';
import HomeImageManager from './pages/HomeImageManager';
import NowPaymentsManagement from './pages/NowPaymentsManagement';
import NowPaymentsInvoiceDetail from './pages/NowPaymentsInvoiceDetail';
import RewardsManagement from './pages/RewardsManagement';


// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const checkAuth = async () => {
      const token = AdminTokenService.getToken();
      
      if (!token) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        await AdminAuthAPI.getProfile();
        setIsAuthenticated(true);
      } catch (error) {
        AdminTokenService.clear();
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [location.pathname]);

  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  React.useEffect(() => {
    const checkAuth = () => {
      const token = AdminTokenService.getToken();
      setIsAuthenticated(!!token);
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          
          {/* Protected Routes */}
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="users/:userId" element={<UserDetail />} /> 
            <Route path="wallet" element={<WalletsPage />} />
            <Route path="wallets/:walletId" element={<WalletDetail />} />
            <Route path="kyc" element={<KYC />} />
            <Route path="kyc/:kycId" element={<KYCDetail />} />
            <Route path="settings" element={<Settings />} />
            <Route path="admins" element={<Admins />} />
            <Route path="admins/:adminId" element={<AdminDetail />} />
            <Route path="deposits" element={<DepositManagement />} />
            <Route path="withdrawals" element={<WithdrawalManagement />} />
            <Route path="wallet-settings" element={<WalletSettings />} />
            <Route path="btc-settings" element={<BTCSettings />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/bets" element={<Bets />} />
            <Route path="/home-images" element={<HomeImageManager />} />
            <Route path="/admin/nowpayments" element={<NowPaymentsManagement />} />
            <Route path="/admin/nowpayments/invoice/:invoiceId" element={<NowPaymentsInvoiceDetail />} />
            <Route path="/admin/rewards" element={<RewardsManagement />} />
          </Route>

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </div>
  );
}

export default App;